import { PerspectiveCamera, Vector3, MathUtils, MOUSE, TOUCH } from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { settings } from '../config/settings.js';
import { clamp, damp } from '../utils/math.js';
import { LAYER } from './Layers.js';

const _dir = new Vector3();
const _desiredTarget = new Vector3();

/**
 * Third-person orbit rig.
 *
 * - Left mouse is reserved for drawing, so orbiting is bound to right-drag.
 * - The distance always resolves back to `settings.camera.distance`, so framing
 *   stays consistent no matter where the orbit target drifts. The wheel zooms by
 *   writing that same setting, which means zoom keeps working while the rig is
 *   following an ability, and the editor slider stays the single source of truth.
 * - The rig gently drifts its look-at point toward whatever ability is casting.
 * - After a cast or after the user stops orbiting / zooming, the rig smoothly
 *   re-centres to the default third-person distance via an auto-recentre timer.
 */
export class CameraRig {
  constructor(domElement) {
    this.camera = new PerspectiveCamera(
      settings.camera.fov,
      window.innerWidth / window.innerHeight,
      0.1,
      400
    );
    this.camera.position.set(-3.5, 4.0, 6.0); // third-person spawn position
    this.camera.layers.enable(LAYER.VFX);

    this.controls = new OrbitControls(this.camera, domElement);
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.075;
    this.controls.enablePan = false;
    this.controls.enableZoom = false; // the wheel drives settings.camera.distance instead
    this.controls.minPolarAngle = settings.camera.minPolar;
    this.controls.maxPolarAngle = settings.camera.maxPolar;
    this.controls.rotateSpeed = 0.65;

    // Free the left button for path drawing.
    this.controls.mouseButtons = { LEFT: null, MIDDLE: null, RIGHT: MOUSE.ROTATE };
    // On touch/mobile: 1-finger drags orbit camera, 2-fingers pinch zoom/rotate.
    this.controls.touches = { ONE: TOUCH.ROTATE, TWO: TOUCH.DOLLY_ROTATE };

    this.anchor = new Vector3(0, 0, 0); // the character
    this.focus = new Vector3(0, 0, 0); // point of interest (ability head)
    this.focusWeight = 0;
    this.shakeOffset = new Vector3();
    this.shakeRoll = 0;

    this.controls.target.set(0, settings.camera.targetHeight, 0);
    this.controls.update();

    // Actual distance, eased toward settings.camera.distance so a wheel flick
    // glides instead of snapping.
    this.distance = settings.camera.distance;

    // Auto-recentre state - default third-person distance is 7.5.
    this._recentreTimer = 0;   // seconds remaining before the glide home starts
    this._isOrbiting = false;  // true while the user holds right-drag

    // While the user drags, suppress any pending recentre.
    // When they let go, start a 2.5 s countdown.
    this.controls.addEventListener('start', () => {
      this._isOrbiting = true;
      this._recentreTimer = 0;
    });
    this.controls.addEventListener('end', () => {
      this._isOrbiting = false;
      this._recentreTimer = 2.5;
    });

    this.domElement = domElement;
    this._onWheel = this._onWheel.bind(this);
    domElement.addEventListener('wheel', this._onWheel, { passive: false });
  }

  /** Wheel zoom. Multiplicative, so each notch feels the same at any distance. */
  _onWheel(event) {
    event.preventDefault();

    const cam = settings.camera;
    // Firefox reports lines (deltaMode 1) and pages (2) rather than pixels.
    const scale = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? 100 : 1;
    const delta = (event.deltaY * scale) / 100;

    cam.distance = clamp(
      cam.distance * Math.exp(delta * 0.12 * cam.zoomSpeed),
      cam.minDistance,
      cam.maxDistance
    );

    // Zoom counts as manual movement - restart the recentre countdown.
    this._recentreTimer = 2.5;
  }

  /** Point the rig should orbit around (character position). */
  setAnchor(x, y, z) {
    this.anchor.set(x, y, z);
  }

  /** Nudge the look-at point toward an ability. weight 0..1, decays on its own. */
  lookAt(point, weight = 1) {
    this.focus.copy(point);
    this.focusWeight = Math.max(this.focusWeight, weight);
  }

  /** Switch touch behavior between free camera orbit and skillshot aiming */
  setAiming(isAiming) {
    if (isAiming) {
      // While aiming a skillshot, 1-finger aims the ground indicator; 2-fingers still rotate/zoom
      this.controls.touches = { ONE: null, TWO: TOUCH.DOLLY_ROTATE };
    } else {
      // While idle, 1-finger drags orbit camera smoothly; 2-fingers zoom/rotate
      this.controls.touches = { ONE: TOUCH.ROTATE, TWO: TOUCH.DOLLY_ROTATE };
    }
  }

  /** Reset camera view back to default third-person angle */
  resetView() {
    this.controls.target.set(0, settings.camera.targetHeight, 0);
    this.camera.position.set(-3.5, 4.0, 6.0);
    this.distance = 7.5;
    settings.camera.distance = 7.5;
    this.controls.update();
  }

  /**
   * Arm a recentre to default third-person distance after delay seconds.
   * Call this from App after every spell cast.
   * @param {number} [delay=2.5] seconds before the glide home starts
   */
  recentre(delay = 2.5) {
    this._recentreTimer = delay;
  }

  _calculateResponsiveFov(baseFov, aspect) {
    if (aspect < 1.25) {
      // Narrow / portrait / mobile screens: scale vertical FOV to maintain horizontal coverage
      const targetHFOV = 70 * (Math.PI / 180);
      const vFovRad = 2 * Math.atan(Math.tan(targetHFOV / 2) / Math.max(aspect, 0.45));
      return MathUtils.clamp(vFovRad * (180 / Math.PI), baseFov, 75);
    }
    if (aspect > 2.3) {
      // 32:9 ultra-wide displays: slightly tighten vertical FOV to prevent peripheral stretching
      return Math.max(50, baseFov - 4);
    }
    return baseFov;
  }

  _updateFov() {
    const baseFov = settings.camera.fov;
    const aspect = this._aspect ?? (this.camera.aspect || 1);
    const targetFov = this._calculateResponsiveFov(baseFov, aspect);
    if (Math.abs(this.camera.fov - targetFov) > 0.01) {
      this.camera.fov = targetFov;
      this.camera.updateProjectionMatrix();
    }
  }

  update(dt) {
    const cam = settings.camera;

    if (this._lastSettingsFov !== cam.fov) {
      this._lastSettingsFov = cam.fov;
      this._updateFov();
    }
    this.controls.minPolarAngle = cam.minPolar;
    this.controls.maxPolarAngle = cam.maxPolar;

    // Blend the orbit target between the character and any active ability.
    const blend = MathUtils.clamp(this.focusWeight * cam.autoFrame, 0, 0.85);
    _desiredTarget.copy(this.anchor);
    _desiredTarget.y += cam.targetHeight;
    _desiredTarget.lerp(this.focus, blend);

    this.controls.target.set(
      damp(this.controls.target.x, _desiredTarget.x, cam.damping, dt),
      damp(this.controls.target.y, _desiredTarget.y, cam.damping, dt),
      damp(this.controls.target.z, _desiredTarget.z, cam.damping, dt)
    );

    this.focusWeight = damp(this.focusWeight, 0, 0.08, dt);

    this.controls.update();

    // Auto-recentre: count down the timer, then write the default distance back
    // into settings so the existing damp() below glides the camera home smoothly.
    if (!this._isOrbiting && this._recentreTimer > 0) {
      this._recentreTimer -= dt;
      if (this._recentreTimer <= 0) {
        this._recentreTimer = 0;
        cam.distance = clamp(7.5, cam.minDistance, cam.maxDistance);
      }
    }

    // Enforce the orbit distance (zoom and the editor slider both land here).
    this.distance = damp(this.distance, cam.distance, cam.zoomDamping, dt);
    _dir.copy(this.camera.position).sub(this.controls.target);
    const len = _dir.length() || 1;
    _dir.multiplyScalar(1 / len);
    this.camera.position.copy(this.controls.target).addScaledVector(_dir, this.distance);

    // Camera shake is additive and applied after the controls have settled.
    if (this.shakeOffset.lengthSq() > 0) {
      this.camera.position.add(this.shakeOffset);
      this.camera.rotateZ(this.shakeRoll);
    }
  }

  resize(width, height) {
    this._aspect = width / Math.max(height, 1);
    this.camera.aspect = this._aspect;
    this._updateFov();
  }

  dispose() {
    this.domElement.removeEventListener('wheel', this._onWheel);
    this.controls.dispose();
  }
}
